// src/ProductsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PRODUCTS from "./data/products.json";
import { useCart } from "./contexts/CartContext";
import { useTheme } from "./contexts/ThemeContext";
import { Heart, ShoppingCart } from "lucide-react";
import logo from "./assets/logo.png";

export default function ProductsPage() {
  const navigate = useNavigate();
  const { cart, addItem, removeItem, changeQty, itemCount } = useCart();
  const { darkMode, setDarkMode } = useTheme();

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem("bb_fav_v1");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("bb_fav_v1", JSON.stringify(favorites));
    } catch {}
  }, [favorites]);

  const categories = useMemo(
    () => ["All", ...new Set(PRODUCTS.map((p) => p.category))],
    []
  );

  function qtyInCart(productId) {
    const found = cart.find((p) => p.id === productId);
    return found?.qty || 0;
  }

  function handleAdd(product) {
    addItem(product, 1);
  }

  function handleDecrement(productId) {
    const q = qtyInCart(productId);
    if (q <= 1) removeItem(productId);
    else changeQty(productId, -1);
  }

  function handleIncrement(product) {
    addItem(product, 1);
  }

  function toggleFavorite(id) {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  function openModal(product) {
    setSelectedProduct(product);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setSelectedProduct(null);
}




  const filteredProducts = PRODUCTS.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      q === "" ||
      p.name.toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q);
    const matchesCategory =
      activeCategory === "All" ? true : p.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans">

        {/* HEADER */}
        <header className="w-full h-16 md:h-20 shadow-sm bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-4 gap-4">

            {/* Mobile Back Button */}

            {/* Home Icon */}
            <button
              onClick={() => navigate("/")}
              className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 h-6 text-pink-600 dark:text-pink-300"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-10 0h10"
                />
              </svg>
            </button>




            {/* LOGO */}
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img
                src={logo}
                alt="Bubble Butter Logo"
                className="h-14 md:h-16 object-contain"
              />
              <h1 className="text-xl md:text-2xl font-bold text-pink-600 dark:text-pink-300">
                Bubble Butter
              </h1>
            </div>

            {/* Desktop Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="hover:text-pink-600 dark:hover:text-pink-300"
              >
                Home
              </button>
              <button
                onClick={() => navigate("/cart")}
                className="relative px-3 py-2 rounded-lg bg-pink-600 text-white shadow"
              >
                <ShoppingCart size={18} />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs px-2 rounded-full">
                    {itemCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>



            </div>

            {/* Mobile Cart */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => navigate("/cart")}
                className="relative px-3 py-2 rounded-md bg-pink-600 text-white"
              >
                🛒
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs px-2 rounded-full">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* SEARCH & CATEGORY FILTERS */}
        <div className="sticky top-16 md:top-20 z-40 bg-white dark:bg-gray-900/80 px-4 py-2 shadow-sm">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
            {/* Search Bar */}
            <input
              type="search"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={25}
              className="w-full md:max-w-[25ch] p-3 border rounded-xl shadow-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 focus:outline-none"
            />

            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-sm border ${
                    c === activeCategory
                      ? "bg-pink-600 text-white border-pink-600"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <main className="max-w-6xl mx-auto px-4 mt-6 pb-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-10 text-gray-500 dark:text-gray-400">
                No products found.
              </div>
            ) : (
              filteredProducts.map((product) => {
                const qty = qtyInCart(product.id);
                const fav = favorites.includes(product.id);
                const stock =
                  typeof product.stock === "number" ? product.stock : 9999;

                return (
                  <div
                    key={product.id}
                    className="relative p-4 rounded-2xl shadow border bg-white dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transform hover:-translate-y-1 transition"
                  >
                    <div className="relative">
                      <img
                        src={product.img}
                        alt={product.name}
                        className="w-full h-48 object-cover rounded-xl cursor-pointer"
                        //className="w-full aspect-[3/2] object-contain bg-white rounded-xl cursor-pointer"

                        onClick={() => openModal(product)}
                      />



                      <div className="absolute left-3 top-3 bg-white/90 dark:bg-gray-800/90 px-3 py-1 rounded-full text-sm font-semibold text-pink-600 dark:text-pink-300">
                        ₹{product.price}
                      </div>

                      <button
                        onClick={() => toggleFavorite(product.id)}
                        aria-label={
                          fav ? "Remove from wishlist" : "Add to wishlist"
                        }
                        className="absolute right-3 top-3 p-1 rounded-full bg-white/90 dark:bg-gray-800/90"
                      >
                        {fav ? (
                          <Heart size={18} className="text-red-500 fill-red-500" />
                        ) : (
                          <Heart size={18} className="text-gray-500" />
                        )}
                      </button>
                    </div>

                    <h3 className="mt-4 text-lg font-semibold dark:text-gray-100">
                      {product.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {product.description}
                    </p>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="text-sm">
                        {stock === 0 ? (
                          <span className="text-red-500 font-medium">Out of Stock</span>
                        ) : stock <= 10 ? (
                          <span className="text-orange-500 font-medium">
                            Only {stock} left!
                          </span>
                        ) : (
                          <span className="text-green-500 font-medium">In Stock</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {qty > 0 ? (
                          <div className="flex items-center gap-2 bg-white dark:bg-gray-700 px-2 py-1 rounded-full shadow-sm">
                            <button
                              onClick={() => handleDecrement(product.id)}
                              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-sm"
                            >
                              −
                            </button>
                            <div className="px-3 text-sm font-medium">{qty}</div>
                            <button
                              onClick={() => handleIncrement(product)}
                              className="px-2 py-1 rounded-md bg-pink-600 text-white text-sm"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            disabled={stock === 0}
                            onClick={() => handleAdd(product)}
                            className={`px-3 py-2 rounded-xl text-white text-sm transition ${
                              stock === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-pink-600 hover:bg-pink-700"
                            }`}
                          >
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </main>

        {/* Floating Cart Button */}
        <button
          onClick={() => navigate("/cart")}
          className="fixed right-4 bottom-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-pink-600 text-white shadow-lg"
        >
          <ShoppingCart size={18} />
          <span className="hidden sm:inline">Cart</span>
          {itemCount > 0 && (
            <span className="ml-2 bg-yellow-400 text-black text-xs px-2 rounded-full">
              {itemCount}
            </span>
          )}
        </button>
        {/* PRODUCT MODAL */}
        {modalOpen && selectedProduct && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 pt-16 max-w-md w-full relative shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xl bg-white dark:bg-gray-800 rounded-full p-1 shadow"
              >
                ✕
              </button>

              {/* Image */}
              <img
                src={selectedProduct.img}
                alt={selectedProduct.name}
                //className="h-48 w-full object-cover rounded-xl mb-4"
                className="h-52 w-full object-cover rounded-xl mb-4"

                //className="h-64 md:h-72 w-full object-contain rounded-xl mb-4 bg-white"
                //className="w-full aspect-[3/2] object-contain rounded-xl mb-4 bg-gray-50"

              />

              {/* Name */}
              <h4 className="text-2xl font-bold mb-2">{selectedProduct.name}</h4>

              {/* Price + Measurement */}
              <div className="flex items-center justify-between mb-2 text-gray-700 dark:text-gray-300 px-1">
                <p className="font-semibold text-lg">₹{selectedProduct.price}</p>
                {selectedProduct.unit && (
                  <p className="text-sm opacity-80 pr-2">{selectedProduct.unit}</p>
                )}
              </div>

              {/* Short Description */}
              <p className="mb-3 text-gray-700 dark:text-gray-300">
                {selectedProduct.description || "No description available."}
              </p>

              {/* Long Description */}
              {selectedProduct.longDescription && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed whitespace-pre-line">
                  {selectedProduct.longDescription}
                </p>
              )}

              {/* Fav + Cart */}
              <div className="flex justify-between items-center mb-3">
                {/* Favorite */}
                <button
                  onClick={() => toggleFavorite(selectedProduct.id)}
                  className="p-3 rounded-full bg-gray-100 dark:bg-gray-700"
                >
                  {favorites.includes(selectedProduct.id) ? (
                    <Heart className="text-red-500 fill-red-500" size={20} />
                  ) : (
                    <Heart className="text-gray-500" size={20} />
                  )}
                </button>

                {/* Add to cart / Qty */}
                <div>
                  {qtyInCart(selectedProduct.id) > 0 ? (
                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full max-w-fit">
                      <button onClick={() => handleDecrement(selectedProduct.id)} className="px-2 py-1 rounded-md bg-gray-200 dark:bg-gray-800 text-sm">−</button>
                      <div className="px-3 text-sm font-medium">{qtyInCart(selectedProduct.id)}</div>
                      <button onClick={() => handleIncrement(selectedProduct)} className="px-2 py-1 rounded-md bg-pink-600 text-white text-sm">+</button>
                    </div>
                  ) : (
                    <button
                      disabled={selectedProduct.stock === 0}
                      onClick={() => handleAdd(selectedProduct)}
                      className={`px-4 py-2 rounded-xl text-white text-sm transition max-w-fit ${
                        selectedProduct.stock === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-pink-600 hover:bg-pink-700"
                      }`}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              </div>

              {/* Go to products page */}
              <button
                onClick={() => {
                  navigate("/products");
                  closeModal();
                }}
                className="w-full px-4 py-3 mt-2 rounded-xl text-white bg-pink-600 hover:bg-pink-700 transition text-center"
              >
                Close
              </button>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}
